<script setup>
import darkBlueLogo from 'assets/xprt-dev-logo_v2_dark-blue.svg';
import invertedLogo from 'assets/xprt-dev-logo_v2_dark.svg';
import lightLogo from 'assets/xprt-dev-logo_v2_light.svg';
import { useQuasar } from 'quasar';
import AppRoute from 'src/router/app-route';
import { Route } from 'src/router/routes';
import { computed } from 'vue';

const $props = defineProps({
    width: {
        type: [String, Number],
        default: undefined
    },
    height: {
        type: [String, Number],
        default: undefined
    },
    inverted: {
        type: Boolean,
        default: false
    },
    lazyLoad: {
        type: Boolean,
        default: false
    },
    noLink: {
        type: Boolean,
        default: false
    }
});

const $q = useQuasar();

const logoSource = computed(() => {
    if ($props.inverted) return invertedLogo;
    return $q.dark.isActive ? darkBlueLogo : lightLogo;
});

const wrapperComponent = computed(() => $props.noLink ? 'div' : 'router-link');

// The logo goes home for everyone, as its label says. A logged-in member reaches My Market from the header instead.
const wrapperComponentBind = computed(() => $props.noLink ? {} : { to: AppRoute.build(Route.Home) });

const computedWidth = computed(() => {
    if ($props.width) return normalizeDimension($props.width);
    if ($props.height) return 'auto';
    return '210px'; // Default width
});

const computedHeight = computed(() => {
    if ($props.height) return normalizeDimension($props.height);
    if ($props.width) return 'auto';
    return '42px'; // Default height
});

const attrWidth = computed(() => stripPxSuffix(computedWidth.value));
const attrHeight = computed(() => stripPxSuffix(computedHeight.value));

function normalizeDimension(value) {
    return typeof value === 'number' ? `${value}px` : value;
}

function stripPxSuffix(value) {
    if (typeof value === 'number') return value;
    if (typeof value === 'string' && value !== 'auto') {
        const parsed = parseInt(value, 10);
        return isNaN(parsed) ? undefined : parsed;
    }
    return undefined;
}
</script>

<template>
    <div class="app-logo">
        <component
            :is="wrapperComponent"
            :aria-label="noLink ? undefined : 'Go to Xprt Dev home'"
            class="no-hover-decoration"
            v-bind="wrapperComponentBind"
        >
            <img
                :height="attrHeight"
                :loading="lazyLoad ? 'lazy' : undefined"
                :src="logoSource"
                :width="attrWidth"
                alt="Xprt Dev Logo"
            />
        </component>
    </div>
</template>

<style lang="scss" scoped>
.app-logo {
    display: inline-block;
    height: v-bind(computedHeight);
    line-height: 0;
    width: v-bind(computedWidth);

    a, div {
        display: block;
        height: 100%;
        line-height: 0;
        white-space: nowrap;
        width: 100%;
    }

    img {
        display: block;
        height: 100%;
        object-fit: contain;
        width: 100%;
    }
}
</style>
